// src/pages/payments/ActivationPending.jsx
// FASE 3.A — S-02 Activación pendiente (pixel-perfect del mockup v4).
//
// FIX preventivo producción: polling automático para destrabar pagos
// cuando el webhook flowConfirm de Flow.cl no llega o llega solo con
// status pending. Tres mecanismos en paralelo:
//
// 1. onSnapshot del último payment del user (escucha cambios en Firestore
//    en tiempo real → cubre Escenario A donde el webhook llegó pero el
//    frontend no lo detectó).
//
// 2. Polling cada 30s a userCheckPaymentStatus({ commerceOrder }) que
//    consulta /payment/getStatus directo a Flow y, si está paid,
//    actualiza Firestore (cubre Escenarios B/C donde el webhook nunca
//    llegó o llegó con status=1 pending).
//
// 3. Botón "Verificar pago ahora" que dispara un poll inmediato sin
//    esperar el intervalo.
//
// Tras 10 minutos sin éxito, se muestra opción de contactar soporte.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import { FlowHeader } from './_paymentsShared';
import { useLogoutFlow } from '../../auth/useLogoutFlow';
import { useSmartBack } from '../../auth/useSmartBack';
import SplashScreen from '../../components/SplashScreen';

const POLL_INTERVAL_MS = 30000;   // 30s entre polls
const INITIAL_POLL_DELAY_MS = 5000; // primer poll a los 5s
const MAX_POLL_ATTEMPTS = 20;      // 20 × 30s = 10 min de polling antes de mostrar soporte

export default function ActivationPending() {
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { user } = useAuth();
  const { showLogoutSplash, startLogout, handleLogoutComplete } = useLogoutFlow();
  const [secs, setSecs] = useState(0);
  const [commerceOrder, setCommerceOrder] = useState(null);
  const [paymentRejected, setPaymentRejected] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [showSupportOption, setShowSupportOption] = useState(false);
  const [lastCheckMsg, setLastCheckMsg] = useState('');
  const intervalRef = useRef(null);

  // Timer del "tiempo transcurrido" del card.
  useEffect(() => {
    const i = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(i);
  }, []);

  // 1. onSnapshot del último payment del user: detecta cambios paid/rejected
  //    en Firestore en tiempo real (cubre tanto el webhook normal como
  //    nuestro polling backend).
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(firestore, 'payments'),
      where('uid', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) return;
        const doc = snap.docs[0];
        const data = doc.data();
        setCommerceOrder(doc.id);
        if (data.status === 'paid') {
          // Auto-redirect al onboarding de Telegram.
          navigate('/conectar-telegram', { replace: true });
        } else if (data.status === 'rejected') {
          setPaymentRejected(true);
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
      },
      (err) => {
        console.error('[ActivationPending] snapshot error:', err);
      }
    );
    return () => unsub();
  }, [user, navigate]);

  // 2. Polling: llama userCheckPaymentStatus, que consulta Flow getStatus y
  //    actualiza Firestore si el pago está paid. El onSnapshot de arriba se
  //    encarga de redirigir cuando ve el cambio.
  const checkPaymentStatus = useCallback(async () => {
    if (!commerceOrder || polling) return;
    setPolling(true);
    setLastCheckMsg('Verificando con Flow.cl…');
    try {
      const callable = httpsCallable(functions, 'userCheckPaymentStatus');
      const result = await callable({ commerceOrder });
      const status = result.data && result.data.status;
      console.log('[ActivationPending] poll result:', result.data);
      if (status === 'paid') {
        setLastCheckMsg('¡Pago confirmado! Redirigiendo…');
      } else if (status === 'rejected') {
        setLastCheckMsg('El pago fue rechazado.');
        setPaymentRejected(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        setLastCheckMsg('Aún en proceso. Reintentaremos en 30s.');
      }
    } catch (err) {
      console.error('[ActivationPending] poll error:', err);
      setLastCheckMsg('Error verificando. Reintentaremos en 30s.');
    } finally {
      setPolling(false);
      setPollAttempts((p) => p + 1);
    }
  }, [commerceOrder, polling]);

  // Setup del polling cuando ya tenemos commerceOrder.
  useEffect(() => {
    if (!commerceOrder || paymentRejected) return;
    const initialTimeout = setTimeout(checkPaymentStatus, INITIAL_POLL_DELAY_MS);
    intervalRef.current = setInterval(() => {
      checkPaymentStatus();
    }, POLL_INTERVAL_MS);
    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [commerceOrder, paymentRejected, checkPaymentStatus]);

  // 3. Tras MAX_POLL_ATTEMPTS sin éxito, mostrar soporte y detener polling.
  useEffect(() => {
    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
      setShowSupportOption(true);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [pollAttempts]);

  // FIX bug crítico (paywall-trapped-user): escape del estado pendiente.
  const handleSignOut = startLogout;

  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');

  // FIX BUG 2: early return cuando hay logout splash activo.
  if (showLogoutSplash) {
    return <SplashScreen type="logout" onComplete={handleLogoutComplete} />;
  }

  return (
    <div className="mobile-root">
      <Helmet>
        <title>Activación pendiente · Edge Elite</title>
      </Helmet>

      <section className="screen" style={{ paddingTop: 28, paddingBottom: 120 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <FlowHeader back={goBack} title="Estado de cuenta" />
          <button
            type="button"
            onClick={handleSignOut}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              background: 'transparent',
              border: '1px solid var(--line)',
              color: 'var(--muted)',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Cerrar sesión
          </button>
        </div>

        <div
          style={{
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 18,
            padding: 24,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 999,
              background: 'rgba(234,179,8,.14)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 18,
              position: 'relative',
            }}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#EAB308"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            <div
              style={{
                position: 'absolute',
                inset: -4,
                borderRadius: 999,
                border: '2px solid rgba(234,179,8,.3)',
                animation: 'ee-pulse-ring 2s ease infinite',
              }}
            />
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.1em',
              color: '#EAB308',
              marginBottom: 8,
            }}
          >
            Activación pendiente
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              margin: 0,
              marginBottom: 8,
            }}
          >
            Pago recibido, esperando confirmación
          </h2>
          <p
            style={{
              fontSize: 13,
              color: 'var(--muted)',
              margin: 0,
              lineHeight: 1.5,
              maxWidth: 280,
              marginInline: 'auto',
            }}
          >
            Flow.cl ya recibió tu pago. Estamos esperando la confirmación del banco para activar tu cuenta. Esto suele
            tardar entre 1 y 5 minutos.
          </p>
          <div
            style={{
              marginTop: 18,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              background: 'var(--bg-subtle)',
              borderRadius: 999,
              fontSize: 11,
              color: 'var(--muted)',
            }}
          >
            Tiempo transcurrido{' '}
            <span className="num" style={{ fontWeight: 600, color: 'var(--ink)' }}>
              {mm}:{ss}
            </span>
          </div>
        </div>

        <div
          style={{
            marginTop: 22,
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: 16,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            Tu orden
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Plan</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Trimestral · 3 meses</span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Monto</span>
            <span style={{ fontSize: 13, fontWeight: 600 }} className="num">
              $38.970
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Método</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Visa ···· 4821</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Referencia</span>
            <span style={{ fontSize: 12, fontWeight: 600 }} className="num">
              FL-9X4K2P
            </span>
          </div>
        </div>

        <div
          style={{
            marginTop: 20,
            padding: 16,
            background: 'var(--bg-subtle)',
            borderRadius: 12,
            fontSize: 13,
            color: 'var(--muted)',
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>¿Qué pasa ahora?</strong> Estamos consultando con
          Flow.cl cada 30 segundos. En cuanto se confirme el pago, tu cuenta se activa automáticamente.
        </div>

        {/* Botón verificar pago ahora — fuerza un poll inmediato */}
        <button
          type="button"
          onClick={checkPaymentStatus}
          disabled={polling || !commerceOrder}
          style={{
            marginTop: 14,
            width: '100%',
            padding: '12px 16px',
            borderRadius: 12,
            background: 'transparent',
            border: '1px solid var(--line-strong)',
            color: 'var(--ink)',
            fontSize: 14,
            fontWeight: 600,
            cursor: polling || !commerceOrder ? 'not-allowed' : 'pointer',
            opacity: polling || !commerceOrder ? 0.6 : 1,
            fontFamily: 'inherit',
          }}
        >
          {polling ? 'Verificando…' : 'Verificar pago ahora'}
        </button>
        {lastCheckMsg && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
            {lastCheckMsg}
          </div>
        )}

        {/* Pago rechazado: panel con botón reintentar */}
        {paymentRejected && (
          <div
            style={{
              marginTop: 20,
              padding: 16,
              background: 'rgba(220,38,38,0.08)',
              border: '1px solid rgba(220,38,38,0.3)',
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: '#DC2626', marginBottom: 6 }}>
              El pago fue rechazado
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
              Podés reintentar con la misma tarjeta u otro método de pago.
            </div>
            <button
              type="button"
              onClick={() => navigate('/paywall')}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 10,
                background: 'var(--accent)',
                border: 0,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Reintentar pago
            </button>
          </div>
        )}

        {/* Tras 10 min sin éxito → opción soporte destacada */}
        {showSupportOption && !paymentRejected && (
          <div
            style={{
              marginTop: 20,
              padding: 16,
              background: 'rgba(234,179,8,0.10)',
              border: '1px solid rgba(234,179,8,0.35)',
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: '#92400E', marginBottom: 6 }}>
              El pago está tardando más de lo esperado
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
              Si pagaste y la cuenta no se activó, contactanos con tu número de orden:{' '}
              <strong className="num" style={{ color: 'var(--ink)', fontWeight: 600 }}>
                {commerceOrder || '—'}
              </strong>
            </div>
            <a
              href={`mailto:contacto@edgeelite.cl?subject=Pago%20pendiente&body=Mi%20commerceOrder%20es%3A%20${encodeURIComponent(commerceOrder || '')}`}
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '10px 14px',
                borderRadius: 10,
                background: 'var(--ink)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Contactar soporte
            </a>
          </div>
        )}

        <div style={{ marginTop: 20, fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
          ¿Pasaron más de 10 minutos?{' '}
          <a
            onClick={() => navigate('/soporte')}
            style={{
              color: 'var(--accent)',
              fontWeight: 600,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Contactar soporte
          </a>
        </div>
      </section>

      <div className="sticky-cta">
        <button className="btn-primary" onClick={() => window.location.reload()}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 4v5h-5" />
          </svg>
          Refrescar estado
        </button>
      </div>

      <style>{`
        @keyframes ee-pulse-ring { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(1.4); opacity: 0; } }
      `}</style>
    </div>
  );
}
